import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

export default function ImportOrder() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [orderMeta, setOrderMeta] = useState({});
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  // Fetch vendors on mount
  useEffect(() => {
    axios.get('/api/vendors').then(res => {
      const v = Array.isArray(res.data) ? res.data : (res.data.data || []);
      setVendors(v);
    }).catch(() => {});
  }, []);

  const handleFile = useCallback(async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      setError('Please drop an Excel (.xlsx, .xls) or CSV file');
      return;
    }

    setUploading(true);
    setError('');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post('/api/import/parse', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setParsed(res.data);
      setItems(res.data.items || []);
      setOrderMeta(res.data.orderMeta || {});
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to parse file');
    }
    setUploading(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleFileInput = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'quantity' || field === 'unit_price') {
      updated[index].total_price = (updated[index].quantity || 0) * (updated[index].unit_price || 0);
    }
    setItems(updated);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const totalAmount = items.reduce((sum, item) => sum + (item.total_price || 0), 0);

  const handleConfirm = async () => {
    if (!selectedVendor) { setError('Please select a vendor'); return; }
    if (!orderMeta.order_number) { setError('Order number is required'); return; }
    if (items.length === 0) { setError('No items to import'); return; }

    setCreating(true);
    setError('');
    try {
      const res = await axios.post('/api/import/confirm', {
        vendor_id: selectedVendor,
        order_number: orderMeta.order_number,
        items,
        port_departure: orderMeta.port_departure,
        port_arrival: orderMeta.port_arrival,
        vessel_name: orderMeta.vessel_name,
        ship_date: orderMeta.ship_date,
        generate_pdf: true
      });
      navigate(`/orders/${res.data.order.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create order');
    }
    setCreating(false);
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  return (
    <div>
      <header className="header">
        <div className="header-content">
          <div className="header-left">
            <div className="header-logo">TradeSync</div>
            <nav className="header-nav">
              <Link to="/dashboard">Dashboard</Link>
              <Link to="/vendors">Vendors</Link>
              <Link to="/import" className="active">Import</Link>
            </nav>
          </div>
          <div className="header-right">
            <div className="user-info"><span className="user-badge">{user?.role}</span><span>{user?.email}</span></div>
            <button className="logout-btn" onClick={logout}>Logout</button>
          </div>
        </div>
      </header>

      <main className="dashboard-content">
        <div className="dashboard-header">
          <h2>Import Sales Order</h2>
          <p>Drop an Excel file from your vendor and we'll auto-generate a sales order.</p>
        </div>

        {error && <div className="error-alert" style={{ marginBottom: 16 }}>{error}</div>}

        {!parsed ? (
          /* Drop Zone */
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-input').click()}
            style={{
              border: `3px dashed ${dragging ? 'var(--primary)' : 'var(--gray-300)'}`,
              borderRadius: 16, padding: '60px 40px', textAlign: 'center',
              background: dragging ? 'var(--primary-light)' : 'var(--white)',
              cursor: 'pointer', transition: 'all 0.2s', maxWidth: 600, margin: '0 auto'
            }}
          >
            <input id="file-input" type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleFileInput} />
            <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
            <h3 style={{ color: 'var(--gray-700)', marginBottom: 8 }}>
              {uploading ? 'Parsing your file...' : 'Drop your Excel file here'}
            </h3>
            <p style={{ color: 'var(--gray-400)', fontSize: 14 }}>
              {uploading ? 'AI is mapping your columns...' : 'or click to browse • .xlsx, .xls, .csv supported'}
            </p>
          </div>
        ) : (
          /* Parsed Preview */
          <div>
            {/* Mapping summary */}
            <div style={{ background: 'var(--white)', borderRadius: 12, padding: 20, marginBottom: 16, border: '1px solid var(--gray-200)' }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: 14, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Column Mapping</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {Object.entries(parsed.columnMapping).filter(([, v]) => v !== 'ignore').map(([header, field]) => (
                  <span key={header} style={{ background: 'var(--primary-light)', color: 'var(--primary)', padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 500 }}>
                    {header} → {field}
                  </span>
                ))}
                {Object.entries(parsed.columnMapping).filter(([, v]) => v === 'ignore').map(([header]) => (
                  <span key={header} style={{ background: 'var(--gray-100)', color: 'var(--gray-400)', padding: '4px 12px', borderRadius: 20, fontSize: 13, textDecoration: 'line-through' }}>
                    {header}
                  </span>
                ))}
              </div>
              <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--gray-400)' }}>
                {parsed.totalRows} rows found • {items.length} items extracted
              </p>
            </div>

            {/* Order Meta */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--gray-500)', marginBottom: 4 }}>Vendor *</label>
                <select value={selectedVendor} onChange={(e) => setSelectedVendor(e.target.value)} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--gray-200)', fontSize: 14 }}>
                  <option value="">Select vendor...</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--gray-500)', marginBottom: 4 }}>Order # *</label>
                <input value={orderMeta.order_number || ''} onChange={(e) => setOrderMeta({ ...orderMeta, order_number: e.target.value })} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--gray-200)', fontSize: 14 }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--gray-500)', marginBottom: 4 }}>Port of Departure</label>
                <input value={orderMeta.port_departure || ''} onChange={(e) => setOrderMeta({ ...orderMeta, port_departure: e.target.value })} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--gray-200)', fontSize: 14 }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--gray-500)', marginBottom: 4 }}>Port of Arrival</label>
                <input value={orderMeta.port_arrival || ''} onChange={(e) => setOrderMeta({ ...orderMeta, port_arrival: e.target.value })} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--gray-200)', fontSize: 14 }} />
              </div>
            </div>

            {/* Items Table */}
            <div style={{ background: 'var(--white)', borderRadius: 12, border: '1px solid var(--gray-200)', overflow: 'hidden', marginBottom: 16 }}>
              <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: 14 }}>Line Items ({items.length})</h3>
                <span style={{ fontWeight: 700, color: 'var(--primary)' }}>Total: {formatCurrency(totalAmount)}</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="orders-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>SKU</th><th>Description</th><th>Qty</th><th>Unit Price</th><th>Total</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={item.id || idx}>
                        <td><input value={item.sku} onChange={(e) => updateItem(idx, 'sku', e.target.value)} style={{ border: 'none', background: 'transparent', width: 80, fontSize: 13 }} /></td>
                        <td><input value={item.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} style={{ border: 'none', background: 'transparent', width: '100%', fontSize: 13 }} /></td>
                        <td><input type="number" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', Number(e.target.value))} style={{ border: 'none', background: 'transparent', width: 60, fontSize: 13, textAlign: 'right' }} /></td>
                        <td><input type="number" step="0.01" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', Number(e.target.value))} style={{ border: 'none', background: 'transparent', width: 80, fontSize: 13, textAlign: 'right' }} /></td>
                        <td style={{ fontWeight: 600 }}>{formatCurrency(item.total_price)}</td>
                        <td><button onClick={() => removeItem(idx)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 16 }}>×</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => { setParsed(null); setItems([]); setOrderMeta({}); }} style={{ padding: '12px 24px', borderRadius: 8, border: '1px solid var(--gray-200)', background: 'var(--white)', color: 'var(--gray-600)', cursor: 'pointer', fontWeight: 600 }}>
                ← Start Over
              </button>
              <button onClick={handleConfirm} disabled={creating || !selectedVendor || !orderMeta.order_number} style={{ padding: '12px 32px', borderRadius: 8, border: 'none', background: 'var(--primary)', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 15, flex: 1 }}>
                {creating ? 'Creating Order & Generating PDF...' : '✓ Create Order + Generate PDF'}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
